import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useResponsive } from "../../../../../../hooks/useResponsive";
import { useCard } from "../../../../../../utils/reactQuery/cards";
import { useBoardName } from "../../../../../../utils/reactQuery/boards";
import { useRemoveMultipleParams } from "../../../../../../hooks/useRemoveMultipleParams";
import { descriptionUpdate } from "../../../../../../sevices/apiDescription";
import { clearCheckedIds } from "../../../../../../redux/CheckboxSclice";
import { currentUserId, isAdmin } from "../../../../../../utils/constants";
import { removeHtmlTagsAndEntities } from "../../../../../../utils";
import useToastPromise from "../../../../../../hooks/useToast";
import useMutate from "../../../../../../hooks/useMutate";
import useBoardIdByKey from "../../../../../../hooks/useBoardIdByKey";
import { useLocation } from "react-router-dom";

export const useCardDialog = ({
  cardId,
  setShowDetails,
}) => {
  // States
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [cardTitle, setCardTitle] = useState("");
  const descRef = useRef("");
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Hooks
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [removeMultiParams] = useRemoveMultipleParams();
  const { showToastPromise } = useToastPromise();
  const isMobile = useResponsive("down", "md");
  const isBetween700And900 = useResponsive("between", 700, 900);
  const [cardDescription, setCardDescription] = useState("");

  const descriptionObj = {
    get: () => {
      return cardDescription;
    },
    set: (value) => {
      console.log("description-set", value);
      setCardDescription(value);
    },
  };



  // Data Fetching
  const {
    isLoading: isCardLoading,
    card,
    isError: isCardError,
  } = useCard(cardId);
  const cardDetail = card?.data?.data?.responseData;
  const { boardName } = useBoardName(cardDetail?.board_id);
  const board_permission =
    boardName?.data?.data?.responseData?.board_permission;

  // Permission checks using useMemo
  const permissions = useMemo(() => {
    const checkIsOwnerOrAdmin =
      isAdmin() ||
      cardDetail?.board?.owner_user_id === currentUserId() ||
      board_permission?.is_admin;

    return {
      checkIsOwnerOrAdmin,
      accessForDeleteCard: checkIsOwnerOrAdmin,
      accessForEditCard:
        checkIsOwnerOrAdmin ||
        cardDetail?.reporter === currentUserId() ||
        board_permission?.can_edit_task,
      conditionCheck:
        checkIsOwnerOrAdmin ||
        (cardDetail?.assigned_to_user_id == currentUserId() &&
          board_permission?.can_update_status)
    };
  }, [cardDetail, board_permission]);

  const { mutate: descriptionUpdateMutate } = useMutate(
    descriptionUpdate,
    [],
    () => { },
    () => {
      queryClient.invalidateQueries({
        queryKey: [`card`, cardId],
      });
      queryClient.invalidateQueries({
        queryKey: [`history`, cardId],
      });
    },
    () => { },
  );

  // Effect Hooks
  useEffect(() => {
    setCardTitle(cardDetail?.title);
  }, [card, cardId]);

  // Handlers
  const handlers = {
    handleClose: useCallback(() => {
      if (isFocus) {
        handlers.handleSave();
      }
      removeMultiParams(["card_id", "brd_id"]);
      if (id) {
        if (location.pathname.includes("/feeds/")) {
          navigate(`/feeds/${id}?${searchParams.toString()}`);
        } else {
          navigate(`/board/${id}?${searchParams.toString()}`);
        }
      }
      setShowDetails(false);
      dispatch(clearCheckedIds());
    }, [isFocus, removeMultiParams, setShowDetails]),

    handleSave: useCallback(
      (text) => {
        const currentDescription =
          cardDetail?.description || ""
        const newDescription =
          text || ""

        if (currentDescription !== newDescription) {
          descriptionUpdateMutate({
            card_id: cardId,
            data: { description: text },
          });
        }
      },
      [cardDetail, descRef, descriptionUpdateMutate]
    ),

    handleReload: useCallback(() => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("card_id");
      setSearchParams(newSearchParams);
    }, [searchParams, setSearchParams]),

    handleFilterDrawer: useCallback((value) => {
      setFilterDrawer(value);
    }, []),
  };

  return {
    // States
    filterDrawer,
    descriptionObj,
    isFocus,
    cardTitle,
    descRef,
    isMobile,
    isBetween700And900,
    isCardLoading,
    isCardError,
    card,
    cardDetail,
    board_permission,

    // Permissions
    ...permissions,

    // Handlers
    ...handlers,

    // Setters
    setFilterDrawer,
    setIsFocus,
    setCardTitle,

    // Utils
    showToastPromise,

  };
};
